import {Dialog as LumaAccessibleDialog, DialogPanel as LumaAccessiblePanel, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import {useSpaceAvailability} from '../../../hooks/useWebSocket';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, CalendarClock, CheckCircle2, Clock3, RefreshCw, X } from 'lucide-react';
import {
    EspacioOcupadoHoy,
    fetchEspaciosOcupadosPorFecha,
} from '../../../services/espacioServiciosService.ts';

export const todayOccupancyColors = Object.freeze({
    retryBackground: 'var(--ad-primary-dark, #274f9d)',
    timeText: '#004d7a',
    countText: '#8A3D00',
});

const C = {
    primary: 'var(--ad-primary, #3564db)',
    primaryDark: 'var(--ad-primary-dark, #274f9d)',
    secondary: 'var(--ad-secondary, #3564db)',
    secondaryDark: todayOccupancyColors.timeText,
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark: '#EC8400',
    accentText: todayOccupancyColors.countText,
};

interface Props {
    open: boolean;
    onClose: () => void;
    sucursalId: number;
    fecha?: string;
}

const localDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const TodayOccupiedSpacesModal: React.FC<Props> = ({ open, onClose, sucursalId, fecha }) => {
    const [currentLocalDate, setCurrentLocalDate] = useState(() => localDateKey(new Date()));
    const effectiveDate = fecha ?? currentLocalDate;
    const [spaces, setSpaces] = useState<EspacioOcupadoHoy[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    useSpaceAvailability(branch => { if (open && (!branch || branch === sucursalId)) setRefreshKey(value => value + 1); });
    const requestRef = useRef(0);
    const dialogRef = useRef<HTMLElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open || fecha) return;
        let timer: ReturnType<typeof setTimeout>;
        const scheduleNextDay = () => {
            const now = new Date();
            const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
            timer = setTimeout(() => {
                setCurrentLocalDate(localDateKey(new Date()));
                scheduleNextDay();
            }, Math.max(1, nextDay.getTime() - now.getTime() + 20));
        };
        setCurrentLocalDate(localDateKey(new Date()));
        scheduleNextDay();
        return () => clearTimeout(timer);
    }, [open, fecha]);

    useEffect(() => {
        if (!open) return;
        const requestId = ++requestRef.current;
        setSpaces([]);
        setLoading(true);
        setError(null);
        fetchEspaciosOcupadosPorFecha(effectiveDate, sucursalId)
            .then(data => {
                if (requestId !== requestRef.current) return;
                setSpaces(data.espacios_ocupados);
            })
            .catch(() => {
                if (requestId !== requestRef.current) return;
                setSpaces([]);
                setError('No pudimos cargar la ocupación de hoy. Intenta nuevamente.');
            })
            .finally(() => {
                if (requestId === requestRef.current) setLoading(false);
            });
        return () => { requestRef.current += 1; };
    }, [open, effectiveDate, sucursalId, refreshKey]);

    useEffect(() => {
        if (!open) return;
        previousFocusRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        dialogRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus();
        const handleKeyboard = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab' || !dialogRef.current) return;
            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector));
            if (focusable.length === 0) {
                event.preventDefault();
                dialogRef.current.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyboard);
        return () => {
            document.removeEventListener('keydown', handleKeyboard);
            previousFocusRef.current?.focus();
        };
    }, [open]);

    if (!open) return null;

    const content = (
        <LumaAccessibleDialog open={true} onClose={onClose} data-luma-overlay="true"
            role="dialog"
            onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
            style={{ position:'fixed', inset:0, zIndex:100000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:'rgba(15,23,42,.58)', backdropFilter:'blur(7px)' }}
        >
            <LumaAccessiblePanel data-luma-dialog="true" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="today-occupancy-title" tabIndex={-1} style={{ width:'100%', maxWidth:'760px', maxHeight:'88vh', overflow:'hidden', borderRadius:'24px', background:'var(--ad-surface, #fff)', border:'1px solid rgba(255,255,255,.75)', boxShadow:'0 36px 100px rgba(15,23,42,.28)', fontFamily:"'DM Sans',sans-serif" }} className="card custom-card vx-card">
                <div style={{ height:'5px', background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }}/>
                <header style={{ padding:'22px 24px 18px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'16px', borderBottom:'1px solid var(--ad-line, #f1f5f9)' }}>
                    <div style={{ display:'flex', gap:'13px', alignItems:'center' }}>
                        <div style={{ width:'44px', height:'44px', borderRadius:'14px', background:`linear-gradient(135deg,color-mix(in srgb, ${C.secondary} 9.41%, transparent),color-mix(in srgb, ${C.secondaryLight} 12.55%, transparent))`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <CalendarClock size={21} color={C.secondary}/>
                        </div>
                        <div>
                            <LumaAccessibleTitle id="today-occupancy-title" style={{ margin:0, color:'var(--ad-ink, #0f172a)', fontSize:'1.18rem', fontWeight:900, letterSpacing:'-.025em' }}>Espacios ocupados hoy</LumaAccessibleTitle>
                            <p style={{ margin:'4px 0 0', color:'var(--ad-muted, #64748b)', fontSize:'.78rem', fontWeight:600 }}>Agenda operativa del {new Date(`${effectiveDate}T12:00:00`).toLocaleDateString('es-BO', { day:'numeric', month:'long', year:'numeric' })}</p>
                        </div>
                    </div>
                    <button aria-label="Cerrar espacios ocupados" onClick={onClose} style={{ width:'34px', height:'34px', borderRadius:'10px', border:'1px solid var(--ad-line, #e2e8f0)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}><X size={16} color="var(--ad-muted, #64748b)"/></button>
                </header>

                <div style={{ padding:'18px 24px 24px', overflowY:'auto', maxHeight:'calc(88vh - 105px)' }}>
                    {loading ? (
                        <div role="status" aria-live="polite" aria-label="Cargando ocupación de hoy" style={{ padding:'58px 16px', textAlign:'center' }}>
                            <div style={{ width:'34px', height:'34px', margin:'0 auto 12px', border:`3px solid color-mix(in srgb, ${C.secondary} 14.12%, transparent)`, borderTopColor:C.secondary, borderRadius:'50%', animation:'nbpSpin .7s linear infinite' }}/>
                            <p style={{ color:'var(--ad-muted, #64748b)', fontWeight:700, fontSize:'.84rem' }}>Consultando la agenda de espacios…</p>
                        </div>
                    ) : error ? (
                        <div role="alert" style={{ padding:'30px', textAlign:'center', background:`color-mix(in srgb, ${C.primary} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${C.primary} 12.55%, transparent)`, borderRadius:'16px' }} className="card custom-card vx-card">
                            <p style={{ color:'var(--ad-link, #2854c5)', fontWeight:800, margin:'0 0 14px' }}>{error}</p>
                            <button onClick={() => setRefreshKey(key => key + 1)} style={{ display:'inline-flex', alignItems:'center', gap:'7px', border:'none', borderRadius:'10px', background:C.primaryDark, color:'#fff', padding:'9px 14px', fontWeight:800, cursor:'pointer' }}><RefreshCw size={14}/> Reintentar</button>
                        </div>
                    ) : spaces.length === 0 ? (
                        <div style={{ padding:'44px 20px', textAlign:'center', background:`color-mix(in srgb, ${C.secondaryLight} 5.1%, transparent)`, border:`1.5px dashed color-mix(in srgb, ${C.secondaryLight} 33.33%, transparent)`, borderRadius:'18px' }} className="card custom-card vx-card">
                            <CheckCircle2 size={38} color={C.secondaryLight} style={{ margin:'0 auto 12px' }}/>
                            <p style={{ margin:0, color:'var(--ad-ink, #0f172a)', fontWeight:900 }}>Todos los espacios están libres hoy</p>
                            <p style={{ margin:'5px 0 0', color:'var(--ad-muted, #64748b)', fontSize:'.78rem' }}>No existen reservas ni consumos activos para esta sucursal.</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', marginBottom:'14px', padding:'11px 14px', borderRadius:'13px', background:`color-mix(in srgb, ${C.accentDark} 4.31%, transparent)`, border:`1px solid color-mix(in srgb, ${C.accentDark} 12.55%, transparent)` }} className="card custom-card vx-card">
                                <span style={{ color:'var(--ad-muted, #475569)', fontSize:'.78rem', fontWeight:700 }}>Ocupación confirmada para hoy</span>
                                <strong style={{ color:C.accentText, fontSize:'.8rem' }}>{spaces.length} espacio{spaces.length === 1 ? '' : 's'}</strong>
                            </div>
                            <div style={{ display:'grid', gap:'12px' }}>
                                {spaces.map(space => (
                                    <article key={space.espacio_id} style={{ border:'1.5px solid var(--ad-line, #e2e8f0)', borderRadius:'17px', overflow:'hidden', boxShadow:'0 5px 18px rgba(15,23,42,.045)' }}>
                                        <div style={{ padding:'14px 16px', display:'flex', justifyContent:'space-between', gap:'12px', alignItems:'center', background:'linear-gradient(135deg,#fbfdff,var(--ad-soft, #f8fafc))' }}>
                                            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                                <div style={{ width:'34px', height:'34px', borderRadius:'10px', background:`color-mix(in srgb, ${C.secondary} 7.06%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center' }}><Building2 size={16} color={C.secondary}/></div>
                                                <div>
                                                    <h3 style={{ margin:0, color:'var(--ad-ink, #0f172a)', fontSize:'.92rem', fontWeight:900 }}>{space.espacio_nombre}</h3>
                                                    <p style={{ margin:'2px 0 0', color:'var(--ad-muted, #64748b)', fontSize:'.7rem', fontWeight:600 }}>{space.servicio_nombre}{space.sucursal ? ` · ${space.sucursal}` : ''}</p>
                                                </div>
                                            </div>
                                            <span style={{ borderRadius:'999px', padding:'4px 9px', background:'#fff7ed', border:'1px solid #fdba74', color:'#c2410c', fontSize:'.66rem', fontWeight:900 }}>OCUPADO</span>
                                        </div>
                                        <div style={{ padding:'0 16px 12px' }}>
                                            {space.ocupaciones.map((item, index) => (
                                                <div key={`${item.tipo}-${item.id ?? index}`} style={{ display:'grid', gridTemplateColumns:'minmax(106px,auto) 1fr', alignItems:'center', gap:'12px', padding:'11px 0', borderTop:index === 0 ? '1px solid var(--ad-line, #f1f5f9)' : '1px solid var(--ad-soft, #f8fafc)' }}>
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', color:C.secondaryDark, fontSize:'.76rem', fontWeight:900 }}><Clock3 size={14}/>{item.hora_inicio && item.hora_fin ? `${item.hora_inicio}–${item.hora_fin}` : item.hora_inicio || 'Horario reservado'}</span>
                                                    <span style={{ color:'var(--ad-muted, #475569)', fontSize:'.76rem', fontWeight:700 }}>{item.cliente || 'Ocupación reservada'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );

    return createPortal(content, document.body);
};

export default TodayOccupiedSpacesModal;


